// Shared cached `is_architect` RPC.
//
// Deduplicates the RPC across AuthGuard, InstanceContext, useUserRole and any
// other caller. Cache is keyed by authenticated user id. In-flight calls for
// the same user id are deduped. Resolved values are reused for a short TTL to
// cover the burst of look-ups that fire during the post-login bootstrap.
//
// This is a read/cache optimization only — server-side enforcement (RLS +
// SECURITY DEFINER `is_architect()`) is unchanged. Callers must still
// authorize actions server-side.
import { supabase } from '@/integrations/supabase/client';
import { fetchIsPlatformOwner } from '@/lib/platformOwner';

const TTL_MS = 60_000; // conservative — long enough to collapse the bootstrap burst

type CacheEntry = {
  userId: string;
  promise: Promise<boolean>;
  resolvedAt: number | null;
};

let entry: CacheEntry | null = null;

export function clearIsArchitectCache(): void {
  entry = null;
}

export async function getIsArchitect(userId: string | null | undefined): Promise<boolean> {
  if (!userId) return false;
  const now = Date.now();

  if (entry && entry.userId === userId) {
    if (entry.resolvedAt === null) return entry.promise; // in-flight dedupe
    if (now - entry.resolvedAt < TTL_MS) return entry.promise; // fresh
  }

  // Different user or stale -> refetch
  const promise = (async () => {
    // Canonical global authority is `public.is_platform_owner()`. While the
    // platform-owner migration is unapplied we fall back to `is_architect`,
    // which the migration itself narrows to a transitional wrapper over
    // `is_platform_owner` — so this call means the same thing before and
    // after apply, and the deploy order cannot lock Kris out.
    const owner = await fetchIsPlatformOwner(userId);
    if (owner) return true;
    const { data, error } = await supabase.rpc('is_architect', { _user_id: userId });
    if (error) throw error;
    return data === true;
  })();

  const local: CacheEntry = { userId, promise, resolvedAt: null };
  entry = local;

  promise
    .then(() => {
      if (entry === local) entry.resolvedAt = Date.now();
    })
    .catch(() => {
      if (entry === local) entry = null;
    });

  return promise;
}

// Test/support hook: allow tests to inspect cache size.
export function __getIsArchitectCacheUserId(): string | null {
  return entry?.userId ?? null;
}

// Invalidate cache on sign-out or user change so a different user's session
// never sees the previous user's architect flag.
//
// Guarded against duplicate registration under Vite HMR / test re-imports:
// without this, every module reload would attach another listener and leak
// closures over stale `entry` references.
const AUTH_LISTENER_FLAG = '__isArchitectCacheAuthListenerRegistered__';
type ListenerHost = { [AUTH_LISTENER_FLAG]?: boolean };
const host = (typeof globalThis !== 'undefined' ? (globalThis as ListenerHost) : ({} as ListenerHost));
if (!host[AUTH_LISTENER_FLAG]) {
  host[AUTH_LISTENER_FLAG] = true;
  try {
    supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        clearIsArchitectCache();
        return;
      }
      if (entry && entry.userId !== session.user.id) {
        clearIsArchitectCache();
      }
      // Note: same-user auth events (token refresh, session restore) do NOT
      // clear the cache — preserving in-flight and resolved dedup for both
      // true and false results within the TTL.
    });
  } catch {
    /* no-op in non-browser test environments */
  }
}


